// Work out the origin of the host app that embedded us.
//
// HOST_APP_CONVENTIONS §3.2: "Always pass a concrete target origin." We never
// post to '*', so if we cannot establish the origin with confidence the
// postMessage channel stays disabled and the dashboard simply runs with the
// token it booted with.

/**
 * Normalise a URL string to its origin, or null if it isn't a usable one.
 * @param {string|null|undefined} value
 * @returns {string|null}
 */
export const toOrigin = (value) => {
  if (!value) return null;
  try {
    const { origin } = new URL(value);
    return origin && origin !== 'null' ? origin : null;
  } catch {
    return null;
  }
};

/**
 * Resolve the host origin, in order of confidence:
 *
 *   a) the `hostOrigin` URL param — explicit, and the only one the host can
 *      guarantee (it derives its own postMessage origin from the raw
 *      configured iframe URL, so a literal origin is available to put there);
 *   b) the origin of `document.referrer`, which for an embedded page is the
 *      embedder — absent when the host sets a strict referrer policy;
 *   c) `location.ancestorOrigins[0]` — WebKit/Blink only;
 *   d) null → refresh disabled.
 *
 * @param {Window} [win=window]
 * @returns {string|null}
 */
export function resolveHostOrigin(win = window) {
  const params = new URLSearchParams(win.location.search);

  const fromParam = toOrigin(params.get('hostOrigin'));
  if (fromParam) return fromParam;

  // Only meaningful when we are actually embedded
  const embedded = win.parent && win.parent !== win;
  if (!embedded) return null;

  const fromReferrer = toOrigin(win.document.referrer);
  if (fromReferrer) return fromReferrer;

  const ancestors = win.location.ancestorOrigins;
  if (ancestors && ancestors.length > 0) {
    const fromAncestor = toOrigin(ancestors[0]);
    if (fromAncestor) return fromAncestor;
  }

  return null;
}
